/**
 * Country and currency choices for registration forms (wave W1.5, finding 2).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The server stores a country as its ISO 3166-1 two-letter code and a currency as its
 * ISO 4217 three-letter code. A free text box let Mahmoud type "Poland", which the server
 * refused without naming the field. The forms now offer the list, by NAME, and send the
 * code; the names come from the browser's own Intl data, so nothing is hand-translated.
 */

/** ISO 3166-1 alpha-2, the officially assigned codes. */
const COUNTRY_CODES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO " +
  "BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ " +
  "DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP " +
  "GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG " +
  "KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML " +
  "MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE " +
  "PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL " +
  "SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM " +
  "US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");

export interface Choice {
  readonly code: string;
  readonly name: string;
}

function named(type: "region" | "currency", codes: readonly string[]): Choice[] {
  const names = new Intl.DisplayNames(["en"], { type, fallback: "code" });
  return codes
    .map((code) => ({ code, name: names.of(code) ?? code }))
    .sort((a, b) => a.name.localeCompare(b.name, "en"));
}

export const COUNTRIES: readonly Choice[] = named("region", COUNTRY_CODES);

export const CURRENCIES: readonly Choice[] = named(
  "currency",
  typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("currency") : [],
);

/**
 * A saved value read back as a code: the code itself, or a country NAME typed before the
 * list existed ("Poland" becomes PL). Anything else reads as not set.
 */
export function countryCode(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }
  const upper = trimmed.toUpperCase();
  if (COUNTRIES.some((one) => one.code === upper)) {
    return upper;
  }
  const lower = trimmed.toLowerCase();
  return COUNTRIES.find((one) => one.name.toLowerCase() === lower)?.code ?? "";
}

/** The same for a currency: its code, or its English name. */
export function currencyCode(value: string): string {
  const trimmed = value.trim();
  if (trimmed === "") {
    return "";
  }
  const upper = trimmed.toUpperCase();
  if (CURRENCIES.some((one) => one.code === upper)) {
    return upper;
  }
  const lower = trimmed.toLowerCase();
  return CURRENCIES.find((one) => one.name.toLowerCase() === lower)?.code ?? "";
}
